import { ILendingSession } from './types';

export type ScreenName =
  | 'yourInformation'
  | 'bankStatement'
  | 'assessing'
  | 'loanOffer'
  | 'upfrontPayment'
  | 'confirmingPayment'
  | 'paymentSuccessful'
  | 'settingUpLoan'
  | 'allSet'
  | 'notEligible'
  | 'chooseCheaperProduct'
  | 'closed';

/** What only the SDK knows: things the customer did that the backend has not (or never will) record. */
export interface UiState {
  /** The customer pressed Continue on Your Information in this visit. */
  detailsConfirmed: boolean;
  /** The customer went Back from Bank Statement to change their details. */
  editingDetails: boolean;
  /** The payment SDK reported success; waiting for the gateway to confirm it to the backend. */
  awaitingPaymentConfirmation: boolean;
  /** The customer pressed Continue on Payment Successful. */
  paymentSuccessSeen: boolean;
}

export const initialUi: UiState = {
  detailsConfirmed: false,
  editingDetails: false,
  awaitingPaymentConfirmation: false,
  paymentSuccessSeen: false,
};

/** Statuses in which the customer may still change their details (the backend's CUSTOMER_EDITABLE). */
export const DETAILS_EDITABLE = new Set(['INFORMATION_REQUIRED', 'INITIATED']);

const CLOSED = new Set(['VERIFICATION_FAILED', 'OFFER_REJECTED', 'CANCELLED', 'FAILED']);
const DONE = new Set(['FUNDED', 'COMPLETED']);
const PAID = new Set(['EQUITY_PAYMENT_SUCCESSFUL', 'PROCESSING']);
const PAYING = new Set(['OFFER_ACCEPTED', 'EQUITY_PAYMENT_PENDING', 'EQUITY_PAYMENT_FAILED']);
const OFFERED = new Set(['ELIGIBLE', 'OFFER_PRESENTED']);

export const takesStatement = (session: ILendingSession) => session.journey?.includes('BANK_STATEMENT_UPLOAD') ?? false;

/** Statuses in which the backend takes a statement (its STATEMENT_OPEN, less the failed analysis handled on its own). */
const STATEMENT_OPEN = new Set(['INITIATED', 'VERIFICATION_PENDING', 'STATEMENT_ANALYSIS_PENDING']);

/**
 * The backend is waiting for the customer's statement: none was uploaded, or the last one failed. A BVN check that
 * passes at once moves the application to STATEMENT_ANALYSIS_PENDING before any statement exists.
 */
export const awaitingStatement = (session: ILendingSession) => {
  const statement = session.statement?.status ?? 'NOT_SUBMITTED';
  return STATEMENT_OPEN.has(session.status) && takesStatement(session) && (statement === 'NOT_SUBMITTED' || statement === 'FAILED');
};

/** The screen for where the application is. The backend's session decides; the SDK only adds what it alone knows. */
export const screenFor = (session: ILendingSession, ui: UiState): ScreenName => {
  const { status } = session;

  if (CLOSED.has(status)) {
    return 'closed';
  }
  if (DONE.has(status)) {
    return 'allSet';
  }
  if (PAID.has(status) || session.equityPayment?.status === 'PAID') {
    return ui.paymentSuccessSeen ? 'settingUpLoan' : 'paymentSuccessful';
  }
  if (PAYING.has(status)) {
    return ui.awaitingPaymentConfirmation && session.equityPayment?.status !== 'FAILED' ? 'confirmingPayment' : 'upfrontPayment';
  }
  if (OFFERED.has(status)) {
    return 'loanOffer';
  }
  if (status === 'INELIGIBLE') {
    return session.eligibility?.reasonCode === 'PURCHASE_ABOVE_ELIGIBLE_AMOUNT' ? 'chooseCheaperProduct' : 'notEligible';
  }
  if (status === 'STATEMENT_ANALYSIS_FAILED') {
    return 'bankStatement';
  }
  if (status === 'INFORMATION_REQUIRED') {
    return 'yourInformation';
  }
  if (status === 'INITIATED' && (ui.editingDetails || !ui.detailsConfirmed)) {
    return 'yourInformation';
  }
  if (awaitingStatement(session)) {
    return 'bankStatement';
  }
  return 'assessing';
};

export interface Step {
  label: string;
  screens: ScreenName[];
}

/** The stepper: Bank Statement only when the programme takes one. */
export const stepsFor = (session: ILendingSession): Step[] => [
  { label: 'Your Information', screens: ['yourInformation'] },
  ...(takesStatement(session) ? [{ label: 'Bank Statement', screens: ['bankStatement'] as ScreenName[] }] : []),
  { label: 'Eligibility', screens: ['assessing', 'notEligible', 'chooseCheaperProduct'] },
  { label: 'Loan Offer', screens: ['loanOffer', 'upfrontPayment', 'confirmingPayment'] },
];

/** The 1-based step a screen belongs to, or 0 for screens shown without the stepper. */
export const stepOf = (steps: Step[], screen: ScreenName): number => steps.findIndex((s) => s.screens.includes(screen)) + 1;

/** The backend is working: the SDK polls the session. */
export const WAITING = new Set([
  'VERIFICATION_PENDING',
  'STATEMENT_ANALYSIS_PENDING',
  'ASSESSMENT_PENDING',
  'EQUITY_PAYMENT_PENDING',
  'EQUITY_PAYMENT_SUCCESSFUL',
  'PROCESSING',
]);

/** Nothing changes while the backend waits for the statement only, so the session is left alone until it is uploaded. */
export const shouldPoll = (session: ILendingSession | undefined, ui: UiState) =>
  !!session
  && ((WAITING.has(session.status) && !(session.status === 'STATEMENT_ANALYSIS_PENDING' && awaitingStatement(session)))
    || (ui.awaitingPaymentConfirmation && session.equityPayment?.status !== 'PAID'));
